import externalLinks from './_utils/externalLinks'
import deviceDetect from './_utils/deviceDetect'
import handleFlyoutMenu from './_components/handleFlyoutMenu'
import showHideMenu from './_components/showHideMenu'
import { Dialog } from './_components/dialog/dialog'
import EmblaCarousel, { EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { addPrevNextBtnsClickHandlers } from './_components/carousel/arrow-buttons'
import { addDotBtnsAndClickHandlers } from './_components/carousel/dot-button'
import materialCalculator from './materialCalculator'
import productsFilter from './productsFilter'
import blogPosts from './blogPosts'
import removeTabIndex from './removeTabIndex'
import { addThumbBtnsClickHandlers, addToggleThumbBtnsActive } from './_components/carousel/thumbnails'
import MicroModal from 'micromodal'

/**
 * Mounts the material calculator and blog components
 */
materialCalculator.mount( '[data-v-id="material-calculator"]' )
materialCalculator.mount( '[data-v-id="products-material-calculator"]' )
blogPosts.mount( '[data-v-id="posts-container"]' )
productsFilter.mount( '[data-v-id="products-filter"]' )

/**
 * Sets up the Embla carousel options
 */
const OPTIONS: Partial<EmblaOptionsType> = { axis: 'y', loop: true }
const PRODUCT_OPTIONS: Partial<EmblaOptionsType> = { axis: 'x', loop: true }

/**
 * Add aria-labels to buttons that have
 * no discernible text content.
 */
const a11ySlides = () => {
  const dots = document.querySelectorAll<HTMLElement>( '.embla__dot' )
  dots.forEach( ( dot, index ) => ( dot.ariaLabel = `Show slide ${index + 1}` ) )
}

/**
 * Initializes product carousel with options and sets up
 * button and dot click handlers for navigation.
 */
const emblaProductNode = document.querySelector<HTMLElement>( '.embla__product' )
if ( emblaProductNode ) {
  const viewportNode = emblaProductNode.querySelector<HTMLElement>( '.embla__viewport' )
  const prevBtnNode = emblaProductNode.querySelector<HTMLElement>( '.embla__button--prev' )
  const nextBtnNode = emblaProductNode.querySelector<HTMLElement>( '.embla__button--next' )
  const dotsNode = emblaProductNode.querySelector<HTMLElement>( '.embla__dots' )

  // If the viewport node exists, initialize the carousel
  if ( viewportNode ) {
    const emblaApi = EmblaCarousel( viewportNode, PRODUCT_OPTIONS )

    // If the previous and next buttons exist, add click handlers
    if ( prevBtnNode && nextBtnNode ) {
      const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
        emblaApi,
        prevBtnNode,
        nextBtnNode
      )
      emblaApi.on( 'destroy', removePrevNextBtnsClickHandlers )
    }

    // If the dots node exists, add dot buttons and click
    if ( dotsNode ) {
      const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers( emblaApi, dotsNode )
      emblaApi.on( 'destroy', removeDotBtnsAndClickHandlers )
    }

    // Add aria-labels to slides
    emblaApi.on( 'slidesInView', a11ySlides )
    emblaApi.on( 'init', a11ySlides )

    const emblaThumbsNode = document.querySelector<HTMLElement>( '.embla-thumbs' )

    // If the embla thumbs node exists, initialize the carousel
    if ( emblaThumbsNode ) {
      const viewportThumbsNode = emblaThumbsNode.querySelector<HTMLElement>(
        '.embla-thumbs__viewport'
      )

      if ( viewportThumbsNode ) {
        const emblaApiThumbs = EmblaCarousel( viewportThumbsNode, {
          containScroll: 'keepSnaps',
          dragFree: true,
          align: 'center',
        } )

        // If the thumbs carousel exists, add click handlers
        const removeThumbBtnsClickHandlers = addThumbBtnsClickHandlers( emblaApi, emblaApiThumbs )
        emblaApiThumbs.on( 'init', removeThumbBtnsClickHandlers )
        emblaApi.on( 'destroy', removeThumbBtnsClickHandlers )

        // If the thumbs carousel exists, add active state handlers
        const removeToggleThumbBtnsActive = addToggleThumbBtnsActive( emblaApi, emblaApiThumbs )
        emblaApi.on( 'destroy', removeToggleThumbBtnsActive )

        /**
         * Sets up modal triggers for images
         */
        document
          .querySelectorAll( '[data-modal-open="modal-image"]' )
          .forEach( ( trigger ) => {
            trigger.addEventListener( 'click', () => {
              const modalTitleElement = document.getElementById( 'modal-image-title' )
              if ( modalTitleElement ) {
                modalTitleElement.innerHTML =
                  ( trigger as HTMLElement ).dataset.modalTitle || ''
              }
              const imageContainer = document.querySelector( '#modal-image .image-container' )
              if ( imageContainer ) {
                imageContainer.innerHTML = `<img src="${
                  ( trigger as HTMLElement ).dataset.modalImageFullSizeSrc
                }" alt="Modal Image">`
              }
              MicroModal.show( 'modal-image' )
            } )
          } )
      }
    }
  }
}

/**
 * Initializes main carousel with autoplay functionality
 * and sets up button and dot click handlers for navigation.
 */
const emblaNode = document.querySelector<HTMLElement>( '.embla' )
if ( emblaNode ) {
  const viewportNode = emblaNode.querySelector<HTMLElement>( '.embla__viewport' )
  const prevBtnNode = emblaNode.querySelector<HTMLElement>( '.embla__button--prev' )
  const nextBtnNode = emblaNode.querySelector<HTMLElement>( '.embla__button--next' )
  const dotsNode = emblaNode.querySelector<HTMLElement>( '.embla__dots' )

  // If the viewport node exists, initialize the carousel
  if ( viewportNode ) {
    const emblaApi = EmblaCarousel( viewportNode, OPTIONS, [
      Autoplay( { playOnInit: false, delay: 3000 } ),
    ] )

    if ( prevBtnNode && nextBtnNode ) {
      const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
        emblaApi,
        prevBtnNode,
        nextBtnNode
      )
      emblaApi.on( 'destroy', removePrevNextBtnsClickHandlers )
    }

    if ( dotsNode ) {
      const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers( emblaApi, dotsNode )
      emblaApi.on( 'destroy', removeDotBtnsAndClickHandlers )
    }

    emblaApi.on( 'slidesInView', a11ySlides )
    emblaApi.on( 'init', a11ySlides )
  }
}

/**
 * Detects user device and adjusts settings accordingly.
 */
deviceDetect()

/**
 * Adds external link behaviors for accessibility.
 */
externalLinks()

/**
 * Handles the visibility of the main menu based on user interactions.
 */
showHideMenu()

/**
 * Initializes flyout menu interactions.
 */
handleFlyoutMenu()

/**
 * Initializes dialog components.
 */
Dialog()

/**
 * Removes unnecessary tabindex attributes for better accessibility.
 */
removeTabIndex()

/**
 * Sets up click handlers for timeline entries to toggle active state.
 */
const timelineEntries = document.querySelectorAll( '[data-timeline-entry]' )
if ( timelineEntries ) {
  timelineEntries.forEach( ( entry ) => {
    entry.addEventListener( 'click', () => {
      timelineEntries.forEach( ( entry ) => entry.classList.remove( 'is-active' ) )
      entry.classList.toggle( 'is-active' )
    } )
  } )
}

/**
 * Handles hover interactions for location list items,
 * toggling the active state and associated image visibility.
 */
const locationLisItems = document.querySelectorAll<HTMLDivElement>( '[data-location-item]' )
if ( locationLisItems ) {
  locationLisItems.forEach( ( item ) => {
    item.addEventListener( 'mouseover', () => {
      locationLisItems.forEach( ( item ) => item.classList.remove( 'is-active' ) )
      const locationToShow = document.querySelector(
        `[data-location-image="${item.dataset.location}"]`
      )
      locationToShow?.classList.add( 'is-active' )
      item.classList.toggle( 'is-active' )
    } )
    item.addEventListener( 'mouseout', () => {
      item.classList.remove( 'is-active' )
      const locationToShow = document.querySelector(
        `[data-location-image="${item.dataset.location}"]`
      )
      locationToShow?.classList.remove( 'is-active' )
    } )
  } )
}

/**
 * Sets up an auto-clicker for radio and checkbox inputs,
 * automatically advancing to the next step in the form when changed.
 */
const setupAutoClicker = () => {
  const currentSelectors = document.querySelectorAll(
    '.gchoice input[type="radio"], .gchoice input[type="checkbox"]'
  )
  currentSelectors.forEach( ( radio ) => {
    radio.addEventListener( 'change', () => {
      const nextButton = document.querySelector<HTMLElement>(
        '[id^="gform_page_2"]:not([style]) [id^="gform_next_button"]'
      )
      nextButton?.click()
    } )
  } )
}

/**
 * Observes mutations in the DOM and dynamically sets up
 * auto-click behavior for industry radio buttons.
 */
document.addEventListener( 'DOMContentLoaded', () => {
  const targetNode = document.getElementById( 'gform_wrapper_2' ) // Replace with the correct form wrapper ID
  if ( !targetNode ) return

  const observerConfig = { childList: true, subtree: true }
  setupAutoClicker()

  const callback = ( mutationsList, observer ) => {
    const industryRadios = document.querySelectorAll( '#input_2_5 input[type="radio"]' )
    setupAutoClicker()
    if ( industryRadios.length > 0 ) {
      industryRadios.forEach( ( radio ) => {
        radio.addEventListener( 'change', ( event: Event ) => {
          const dataIndustry = document.querySelector<HTMLDivElement>( '[data-industry]' )
          if ( dataIndustry ) {
            dataIndustry.dataset.industry = ( event.target as HTMLInputElement ).value
            const industryStylePatch = document.getElementById( 'industry-style-patch' )
            if ( industryStylePatch ) {
              industryStylePatch.remove()
            }
            const stylePatch = document.createElement( 'style' )
            stylePatch.id = 'industry-style-patch'
            stylePatch.textContent = `
              .product-choice {
                display: none;
              }
              .product-choice[data-product-industries*="${dataIndustry.dataset.industry}"] {
                display: flex;
              }
            `
            document.head.appendChild( stylePatch )
          }
        } )
      } )
    }
  }

  const observer = new MutationObserver( callback )
  observer.observe( targetNode, observerConfig )
} )
